/**
 * Customer text for a confirm-booking decline.
 * The Moneris message is only "DECLINED" or "CARD DECLINED". The response code and ISO together identify the reason.
 * Codes under 050 are approvals and are not shown here.
 */

export interface MonerisConfirmBody {
	responseCode?: string | null;
	iso?: string | null;
}

interface DeclineCopy {
	message: string;
	canRetry: boolean;
}

const DECLINES: Readonly<Record<string, DeclineCopy>> = {
	'481:51': {
		message: 'Fonds insuffisants. Utilisez une autre carte ou contactez votre institution financière.',
		canRetry: true
	},
	'481:57': {
		message: "Cette transaction n'est pas autorisée pour cette carte. Utilisez un autre moyen de paiement.",
		canRetry: true
	},
	'481:63': {
		message: 'Le paiement a été refusé pour des raisons de sécurité. Contactez votre institution financière.',
		canRetry: false
	},
	'421:04': {
		message: 'Cette carte a été refusée. Ne réessayez pas avec la même carte.',
		canRetry: false
	},
	'485:78': {
		message: "Cette carte n'est pas activée ou est temporairement bloquée. Contactez votre institution financière.",
		canRetry: false
	}
};

const GENERIC_DECLINE: DeclineCopy = {
	message: 'Le paiement a été refusé par votre institution financière.',
	canRetry: true
};

/** Message to show after Confirm/Details fails. Null when the body has no Moneris response code. */
export function monerisConfirmMessage(body: MonerisConfirmBody | null | undefined): string | null {
	const responseCode = normalizeResponseCode(body?.responseCode);
	if (!responseCode) {
		return null;
	}

	const numeric = Number(responseCode);
	if (Number.isInteger(numeric) && numeric >= 0 && numeric <= 49) {
		return null;
	}

	const iso = (body?.iso ?? '').trim();
	return (DECLINES[`${responseCode}:${iso}`] ?? GENERIC_DECLINE).message;
}

function normalizeResponseCode(value: string | null | undefined): string | null {
	const trimmed = (value ?? '').trim();
	if (!/^\d+$/.test(trimmed)) {
		return null;
	}
	return trimmed.padStart(3, '0');
}
